import { CalendarBlank, Clock } from "@/components/ui/icons";
import { CardShell } from "./CardShell";
import { CardActions } from "./CardActions";

export interface CalendarEventData {
  event_id: string;
  title: string;
  start: string;
  end: string;
  web_link: string;
}

interface CalendarEventCardProps {
  event: CalendarEventData;
  /** When true, renders in compact mode for chat messages (no drag handle) */
  compact?: boolean;
  density?: "ultra-compact" | "compact" | "comfortable";
}

export function CalendarEventCard({
  event,
  compact,
  density,
}: CalendarEventCardProps) {
  const timeRange = formatTimeRange(event.start, event.end);

  return (
    <CardShell
      compact={compact}
      density={density}
      icon={<CalendarBlank size={12} className="text-info" />}
      label="calendar"
      telemetryCardType="calendar"
      headerActions={
        <CardActions
          compact={compact}
          title={event.title}
          summary={timeRange}
          url={event.web_link || null}
        />
      }
      contentClassName={`${
        compact || density === "ultra-compact"
          ? "px-2 py-1.5 space-y-1"
          : density === "compact"
            ? "px-2.5 py-2 space-y-1.5"
            : "px-3 py-2.5 space-y-2"
      }`}
    >
      {/* Title */}
      <div className="text-foreground font-bold whitespace-pre-wrap break-words">
        {event.title || "Untitled Event"}
      </div>

      {/* Time range */}
      {timeRange && (
        <div className="flex items-center gap-1.5">
          <Clock size={10} className="text-info shrink-0" />
          <span className="text-foreground/70">{timeRange}</span>
        </div>
      )}
    </CardShell>
  );
}

/** Parse calendar tool output into CalendarEventData[] */
export function parseCalendarResult(
  result: string,
): CalendarEventData[] | null {
  try {
    const parsed = JSON.parse(result);
    if (!parsed || typeof parsed !== "object" || !parsed.ok) return null;

    // Multi-event read result: { events: [...] }
    if (Array.isArray(parsed.events) && parsed.events.length > 0) {
      return parsed.events
        .map((ev: Record<string, unknown>): CalendarEventData | null => {
          if (!ev || typeof ev !== "object") return null;
          const title = String(ev.title ?? ev.summary ?? "");
          const event_id = String(ev.event_id ?? ev.id ?? "");
          if (!title && !event_id) return null;
          return {
            event_id,
            title,
            start: String(ev.start ?? ""),
            end: String(ev.end ?? ""),
            web_link: String(ev.web_link ?? ev.htmlLink ?? ""),
          };
        })
        .filter((e: CalendarEventData | null): e is CalendarEventData =>
          Boolean(e),
        );
    }

    // Single-event write result: { event_id, web_link, tool_name }
    if (parsed.event_id) {
      const toolName = String(parsed.tool_name ?? "");
      const title = toolName.includes("create")
        ? "Event Created"
        : toolName.includes("update")
          ? "Event Updated"
          : toolName.includes("delete")
            ? "Event Deleted"
            : "Calendar Event";
      return [
        {
          event_id: String(parsed.event_id),
          title,
          start: String(parsed.start ?? ""),
          end: String(parsed.end ?? ""),
          web_link: String(parsed.web_link ?? ""),
        },
      ];
    }

    return null;
  } catch {
    return null;
  }
}

function formatTimeRange(start: string, end: string): string {
  if (!start) return "";
  try {
    const s = new Date(start);
    const fmt: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    };
    const startStr = s.toLocaleString("en-US", fmt);
    if (!end) return startStr;
    const e = new Date(end);
    // Same day — only show time for end
    if (s.toDateString() === e.toDateString()) {
      const endTime = e.toLocaleString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      return `${startStr} – ${endTime}`;
    }
    return `${startStr} – ${e.toLocaleString("en-US", fmt)}`;
  } catch {
    return start;
  }
}
